import { supabase } from "@/integrations/supabase/client";
import { slugify } from "@/lib/seo";
import type { Ad } from "@/lib/data";
import { rowToAd, type ListingRow } from "@/lib/listings";

export type Store = {
  id: string;
  owner_id: string | null;
  name: string;
  slug: string | null;
  description: string | null;
  logo_url: string | null;
  banner_url: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  address: string | null;
  work_hours: string | null;
  city: string | null;
  category: string | null;
  delivery: string[];
  plan: string;
  status: string;
  verified: boolean;
  created_at: string;
};

const COLUMNS =
  "id, owner_id, name, slug, description, logo_url, banner_url, phone, whatsapp, email, address, work_hours, city, category, delivery, plan, status, verified, created_at";

export const DELIVERY_OPTIONS = [
  { value: "city", label: "Şəhərdaxili" },
  { value: "regions", label: "Rayonlara" },
  { value: "all", label: "Bütün Azərbaycan" },
] as const;

export const deliveryLabels: Record<string, string> = {
  city: "Şəhərdaxili",
  regions: "Rayonlara",
  all: "Bütün Azərbaycan",
};

export const storeStatusLabels: Record<string, string> = {
  pending: "Təsdiq gözləyir",
  active: "Aktiv",
  blocked: "Bloklanıb",
};

export const storePlanLabels: Record<string, string> = {
  basic: "Standart",
  premium: "Premium",
  vip: "VIP",
};

export function storeSlug(store: Pick<Store, "id" | "name" | "slug">): string {
  return store.slug?.trim() ? store.slug : `${slugify(store.name) || "magaza"}-${store.id}`;
}

export async function fetchActiveStores(): Promise<Store[]> {
  const { data, error } = await supabase
    .from("stores")
    .select(COLUMNS)
    .eq("status", "active")
    .order("verified", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as Store[];
}

export async function fetchStoreBySlug(slug: string): Promise<Store | null> {
  const bySlug = await supabase.from("stores").select(COLUMNS).eq("slug", slug).maybeSingle();
  if (bySlug.data) return bySlug.data as unknown as Store;

  const id = slug.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  )?.[0];
  if (!id) return null;
  const { data, error } = await supabase.from("stores").select(COLUMNS).eq("id", id).maybeSingle();
  if (error) throw error;
  return (data as unknown as Store) ?? null;
}

export async function fetchMyStore(userId: string): Promise<Store | null> {
  const { data, error } = await supabase
    .from("stores")
    .select(COLUMNS)
    .eq("owner_id", userId)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as Store) ?? null;
}

export type StoreInput = {
  name: string;
  slug: string;
  description: string;
  logo_url: string;
  banner_url: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  work_hours: string;
  city: string;
  category: string;
  delivery: string[];
};

export async function saveStore(
  userId: string,
  input: StoreInput,
  storeId?: string,
): Promise<Store> {
  const payload = {
    owner_id: userId,
    name: input.name.trim(),
    slug: slugify(input.slug || input.name) || null,
    description: input.description.trim() || null,
    logo_url: input.logo_url || null,
    banner_url: input.banner_url || null,
    phone: input.phone.trim() || null,
    whatsapp: input.whatsapp.replace(/\D/g, "") || null,
    email: input.email.trim() || null,
    address: input.address.trim() || null,
    work_hours: input.work_hours.trim() || null,
    city: input.city || null,
    category: input.category || null,
    delivery: input.delivery ?? [],
  };

  const query = storeId
    ? supabase.from("stores").update(payload).eq("id", storeId)
    : supabase.from("stores").insert(payload);

  const { data, error } = await query.select(COLUMNS).single();
  if (error) throw error;
  return data as unknown as Store;
}

/** Mağaza sahibinin aktiv elanları */
export async function fetchStoreListings(ownerId: string): Promise<Ad[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(
      "id, user_id, title, description, price, negotiable, category, city, images, badge, status, views, created_at, badge_expires_at, badge_price, specs, phone, whatsapp",
    )
    .eq("user_id", ownerId)
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []).map((r) =>
    rowToAd(r as unknown as ListingRow, undefined, { isStore: true }),
  );
}

/* ---------------- İzləmə (Follow) ---------------- */

export async function fetchFollowerCount(storeId: string): Promise<number> {
  const { count, error } = await supabase
    .from("store_followers")
    .select("id", { count: "exact", head: true })
    .eq("store_id", storeId);
  if (error) throw error;
  return count ?? 0;
}

export async function isFollowingStore(storeId: string, userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("store_followers")
    .select("id")
    .eq("store_id", storeId)
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return Boolean(data);
}

export async function toggleFollowStore(
  storeId: string,
  userId: string,
  following: boolean,
): Promise<boolean> {
  if (following) {
    const { error } = await supabase
      .from("store_followers")
      .delete()
      .eq("store_id", storeId)
      .eq("user_id", userId);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase
    .from("store_followers")
    .insert({ store_id: storeId, user_id: userId });
  if (error) throw error;
  return true;
}

/* ---------------- Rəylər və reytinq ---------------- */

export type StoreReview = {
  id: string;
  store_id: string;
  author_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
};

export async function fetchStoreReviews(storeId: string): Promise<StoreReview[]> {
  const { data, error } = await supabase
    .from("store_reviews")
    .select("id, store_id, author_id, rating, comment, created_at")
    .eq("store_id", storeId)
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as StoreReview[];
}

export async function saveStoreReview(
  storeId: string,
  userId: string,
  rating: number,
  comment: string,
): Promise<void> {
  const { error } = await supabase
    .from("store_reviews")
    .upsert(
      { store_id: storeId, author_id: userId, rating, comment: comment.trim() || null },
      { onConflict: "store_id,author_id" },
    );
  if (error) throw error;
}

export async function deleteStoreReview(storeId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from("store_reviews")
    .delete()
    .eq("store_id", storeId)
    .eq("author_id", userId);
  if (error) throw error;
}

export function averageRating(reviews: StoreReview[] | undefined): number {
  if (!reviews?.length) return 0;
  return reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
}

export type SellerRating = {
  count: number;
  average: number;
  top: StoreReview[];
};

/** Satıcının mağazasının real rəy statistikası (rəy yoxdursa null). */
export async function fetchSellerRating(userId: string): Promise<SellerRating | null> {
  if (!/^[0-9a-f-]{36}$/i.test(userId)) return null;
  try {
    const store = await fetchMyStore(userId);
    if (!store) return null;
    const reviews = await fetchStoreReviews(store.id);
    if (!reviews.length) return null;
    return {
      count: reviews.length,
      average: Math.round(averageRating(reviews) * 10) / 10,
      top: reviews.filter((r) => r.comment?.trim()).slice(0, 3),
    };
  } catch {
    return null;
  }
}